import React, { useEffect, useRef, useState } from "react";
import { api, fmtDate, formatErr } from "@/lib/api";
import { Upload, Paperclip, X, FileText, Image as ImageIcon, Download, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmProvider";

function bytes(n) {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

export default function AttachmentsPanel({ parentType, parentId, testIdPrefix = "att" }) {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const load = async () => {
    if (!parentId) return;
    try {
      const { data } = await api.get("/attachments", { params: { parent_type: parentType, parent_id: parentId } });
      setItems(data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { load(); }, [parentId, parentType]); // eslint-disable-line

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("parent_type", parentType);
      fd.append("parent_id", parentId);
      fd.append("file", file);
      await api.post("/attachments", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Attachment uploaded");
      load();
    } catch (err) { toast.error(formatErr(err)); }
    finally { setUploading(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const confirm = useConfirm();
  const remove = async (id) => {
    const ok = await confirm({
      title: "Delete this attachment?",
      description: "The file will be removed permanently.",
      confirmLabel: "Delete file",
    });
    if (!ok) return;
    try { await api.delete(`/attachments/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const downloadUrl = (a) => {
    const token = localStorage.getItem("equinox_token") || "";
    return `${BACKEND}/api/files/download?path=${encodeURIComponent(a.storage_path)}&auth=${encodeURIComponent(token)}`;
  };

  const isImage = (ct) => (ct || "").startsWith("image/");

  return (
    <div data-testid={`${testIdPrefix}-panel`}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 flex items-center gap-2">
          <Paperclip className="w-3 h-3" /> Attachments ({items.length})
        </div>
        <label
          className="cursor-pointer inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-xs text-white/75 transition"
          data-testid={`${testIdPrefix}-upload-btn`}
        >
          <Upload className="w-3 h-3" />
          {uploading ? "Uploading…" : "Upload file"}
          <input
            ref={inputRef}
            type="file"
            className="hidden"
            onChange={(e) => upload(e.target.files?.[0])}
            disabled={uploading || !parentId}
            data-testid={`${testIdPrefix}-file-input`}
          />
        </label>
      </div>

      <div className="space-y-2">
        {items.map((a) => (
          <div
            key={a.id}
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.05] transition"
            data-testid={`${testIdPrefix}-item-${a.id}`}
          >
            <div className="w-9 h-9 rounded-lg bg-white/[0.06] flex items-center justify-center">
              {isImage(a.content_type) ? <ImageIcon className="w-4 h-4 text-[#007AFF]" /> : <FileText className="w-4 h-4 text-[#007AFF]" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white truncate">{a.filename}</div>
              <div className="text-[10px] text-white/40">{bytes(a.size)} · {fmtDate(a.created_at)}</div>
            </div>
            <a href={downloadUrl(a)} target="_blank" rel="noreferrer" className="text-white/55 hover:text-white p-1.5 rounded-lg hover:bg-white/[0.05]" title="Download">
              <Download className="w-4 h-4" />
            </a>
            <button onClick={() => remove(a.id)} className="text-rose-400/70 hover:text-rose-400 p-1.5 rounded-lg hover:bg-rose-400/10" title="Delete" data-testid={`${testIdPrefix}-delete-${a.id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
        {items.length === 0 && (
          <div className="text-center text-xs text-white/30 py-6 border border-dashed border-white/[0.07] rounded-xl">
            No attachments yet
          </div>
        )}
      </div>
    </div>
  );
}
